import { HTTPError } from 'ky';
import { entries, isEmpty, isNullish, isObjectType, isString, omitBy, values } from 'remeda';
import { toast } from 'sonner';

import { UserRole } from 'v2/constants';
import { FormReward, Nullable, SelectOption } from 'v2/types';
import { RewardModel } from 'v2/types/RewardModel';

export function getOrigin(): string {
  const { hostname, protocol, port } = window.location;

  const isLocalhost = hostname === '127.0.0.1' || hostname === 'localhost';

  return `${protocol}//${hostname}${isLocalhost ? `:${port}` : ''}`.trim();
}

export function fileSrc(fileId: string): string {
  return `${getOrigin()}/fs/v1/file/id/${fileId}`;
}

export async function handleHttpError(error: unknown): Promise<any> {
  if (!(error instanceof HTTPError)) throw error;

  const errorResponse = await error.response.json();

  if (isObjectType(errorResponse) && 'message' in errorResponse && isString(errorResponse.message)) {
    toast.error(errorResponse.message);
  } else {
    throw error;
  }
}

export function isRouteAllowed(userRoles: UserRole[], routeRoles?: UserRole[]): boolean {
  if (isNullish(routeRoles)) {
    return false;
  }

  if (userRoles.includes(UserRole.ADMIN)) {
    return true;
  }

  return routeRoles.some((role) => userRoles.includes(role));
}

export function copyText(text?: Nullable<string>): void {
  if (isNullish(text)) return;

  window.navigator.clipboard.writeText(text).then(() => {
    toast.info('Copied to clipboard 📝');
  });
}

// for arrays with primitive types
export function areArraysEqual<T = string | number | boolean>(arr1: T[], arr2: T[]) {
  if (arr1.length !== arr2.length) return false;

  if (isEmpty(arr1) && isEmpty(arr2)) return true;

  const sortedArr1 = arr1.slice().sort();
  const sortedArr2 = arr2.slice().sort();

  for (let i = 0; i < sortedArr1.length; i++) {
    if (sortedArr1[i] !== sortedArr2[i]) {
      return false;
    }
  }

  return true;
}

export function formatNumber(num: string | number): string {
  return num.toLocaleString('fr-FR');
}

export function percentage(partialValue: number, totalValue: number): number {
  if (partialValue >= totalValue) {
    return 100;
  }

  return (100 * partialValue) / totalValue;
}

export function enumToSelect<T extends string>(enumObj: Record<string, T>): SelectOption<T>[] {
  return values(enumObj).map((value) => ({ id: value, value, label: value }));
}

export function identifyArrayToSelect<T extends string | number>(arr: T[]): SelectOption<T>[] {
  return arr.map((value) => ({ id: value.toString(), value, label: value }));
}

export function mapRewardsToFormRewards(rewards?: RewardModel[]): Required<FormReward>[] {
  if (isNullish(rewards) || isEmpty(rewards)) return [];

  const formRewards = rewards.reduce(
    (acc, cur) => {
      const key = `${cur.type}_${cur.value}`;
      const prev = acc[key];

      acc[key] = prev ? { ...prev, count: prev.count + 1 } : { value: cur.value, type: cur.type, count: 1 };

      return acc;
    },
    {} as Record<string, Required<FormReward>>
  );

  return values(formRewards);
}

// TODO remove when regular filter will be implemented on BE
export function shitFilterMapper(obj: Record<string, any>): string {
  return entries(obj).reduce((acc, [key, value]) => {
    if (isEmpty(value)) {
      return acc;
    }

    acc += `${key},${value}`;
    return acc;
  }, '');
}

export function normalizeSearch(params: Record<string, any>): Record<string, any> {
  return omitBy(params, (item) => {
    if (isString(item)) {
      return isEmpty(item);
    }

    return isNullish(item);
  });
}

export function toggleArrayItem<T>(arr: T[], item: T): T[] {
  const temp = arr;
  const i = temp.indexOf(item);

  if (i === -1) {
    temp.push(item);
  } else {
    temp.splice(i, 1);
  }

  return temp;
}

export function getPaginationCount(pageSize: number, itemsCount: number): number {
  return Math.ceil(itemsCount / pageSize);
}

export function exportFile(blob: Blob | MediaSource, filename?: string): void {
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  document.body.appendChild(a);

  a.style.display = 'none';
  a.href = url;
  a.download = filename || 'exported_file';
  a.click();

  URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export function downloadFileFromLink(imageUrl: string, filename?: string) {
  fetch(imageUrl)
    .then((response) => response.blob())
    .then((blob) => {
      exportFile(blob, filename);
    })
    .catch(() => {
      toast.error('Something went wrong');
    });
}

type KeyDownLikeEvent = {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  preventDefault: () => void;
};

export function preventNonDigitKeyDown(e: KeyDownLikeEvent): void {
  const allowedControlKeys = new Set([
    'Backspace',
    'Delete',
    'Tab',
    'Enter',
    'Escape',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
  ]);

  if (allowedControlKeys.has(e.key)) return;
  if (e.ctrlKey || e.metaKey) return;
  if (/^\d$/.test(e.key)) return;

  e.preventDefault();
}

export function getFileNameFromDispositionHeader(disposition: string | null, defaultName: string): string {
  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
  const matches = disposition !== null ? filenameRegex.exec(disposition) : null;

  if (matches !== null && matches[1]) {
    return decodeURIComponent(matches[1].replace(/['"]/g, ''));
  }

  return defaultName;
}
